// cspell:ignore pageview
import { Injectable } from '@jack-henry/frontend-utils/di';
import { Feature, FeatureFlagService } from '@treasury/domain/services/feature-flags';
import {
    getLocalDateTime,
    getTimezone,
    getTimezoneAbbreviated,
    getTimezoneOffset,
} from '@treasury/utils';
import mixpanel from 'mixpanel-browser';
import { BuildType, ConfigurationService } from '../config';
import { LoggingService } from '../logging';
import {
    AnalyticsEvent,
    AnalyticsEventPayloads,
    IAnalyticsService,
    UserIdentificationData,
} from './analytics.types';
/**
 * The API endpoint used to proxy MixPanel events.
 */
const API_MIXPANEL_ENDPOINT = 'mixpanelproxy';

@Injectable()
export class AnalyticsService implements IAnalyticsService {
    constructor(
        private configService: ConfigurationService,
        private logger: LoggingService,
        private ffService: FeatureFlagService
    ) {}

    private initialized = false;

    public async init() {
        if (this.initialized) {
            return;
        }

        if (!this.configService.analyticsToken) {
            throw new Error('Could not initialize AnalyticsService. No token present.');
        }

        const { apiRoot, analyticsToken, buildType } = this.configService;
        const proxyEnabled = await this.ffService.isEnabled(Feature.MixPanelProxy);
        const api_host = proxyEnabled ? `${apiRoot}/${API_MIXPANEL_ENDPOINT}` : undefined;

        mixpanel.init(analyticsToken, {
            debug: buildType !== BuildType.Production,
            persistence: 'localStorage',
            api_host,
        });

        this.initialized = true;
    }

    /**
     * Associate a unique user with the current analytics session.
     */
    public identify(userData: UserIdentificationData) {
        const { userId, isSuperUser, isAdmin, companyId, institutionUniqueId } = userData;
        const fiId = this.configService.institutionId.toLowerCase();
        const product = this.configService.app;
        const userPayload = {
            'fi-id': fiId,
            'is-admin': isAdmin,
            'is-super-user': isSuperUser,
            'company-id': companyId,
            'institution-unique-id': institutionUniqueId,
        };
        /**
         * [Super properties](https://docs.mixpanel.com/docs/tracking-methods/sdks/javascript#super-properties)
         * sent with every event.
         */
        const superProperties = {
            ...userPayload,
            product,
            'local-date-time': getLocalDateTime(),
            timezone: getTimezone(),
            'timezone-id': getTimezoneAbbreviated(),
            'gmt-offset': getTimezoneOffset(),
        };

        this.guardInit(() => {
            mixpanel.identify(userId.toString());
            mixpanel.register(superProperties);
            mixpanel.people.set(userPayload);
        });
    }

    /**
     * Forget any user associated with the current analytics session.
     */
    public reset() {
        this.guardInit(() => mixpanel.reset());
        this.initialized = false;
    }

    public track<Event extends AnalyticsEvent>(
        eventName: Event,
        ...payload: AnalyticsEventPayloads[Event] extends object
            ? [AnalyticsEventPayloads[Event]]
            : [never?]
    ) {
        this.guardInit(() => mixpanel.track(eventName, payload));
    }

    public trackPageView(route: string) {
        const fiId = this.configService.institutionId.toLowerCase();
        this.guardInit(() =>
            mixpanel.track_pageview({
                route,
                'fi-id': fiId,
            })
        );
    }

    /**
     * Invokes the provided function only if the service has been initialized.
     */
    private guardInit(fn: () => void) {
        if (!this.initialized) {
            this.logger.logWarning('Cannot use analytics service. It has not been initialized.');
            return;
        }

        fn();
    }
}
